import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from 'react-query';
import useBackdrop from '../../hooks/useBackdrop';
import useSnackbar from '../../hooks/useSnackbar';
import { createOrUpdateBundlePackage, deleteBundlePackage, fetchAllBundlePackages, fetchBundlePackages } from '../../services/api/BundlePackage';
import { IBundlePackage, ISuccessResult } from '../../services/types';
import { invalidateQueriesWithDelay } from '../../services/utils';

enum QueryKey {
  AllBundlePackages = 'allBundlePackages',
  BundlePackagesByStoryId = 'bundlePackagesByStoryId',
}

interface IUseBundlePackageProps {
  storyId?: number;
}

interface IUseBundlePackageMutation {
  createOrUpdate: UseMutationResult<IBundlePackage[], Error, { storyId: number; bundles: IBundlePackage[] }, unknown>;
  remove: UseMutationResult<IBundlePackage, Error, IBundlePackage, unknown>;
}

export const useBundlePackages = (): UseQueryResult<IBundlePackage[], Error> =>
  useQuery<IBundlePackage[], Error>(QueryKey.AllBundlePackages, async () => {
    try {
      const [fetchPromise] = fetchAllBundlePackages();
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return Object.values(result.data)
        .reduce((acc, bundles) => {
          return [...acc, ...bundles];
        }, [])
        .sort((a, b) => (a.id! > b.id! ? -1 : 1));
    } catch (e) {
      throw e;
    }
  });

export const useBundlePackage = (
  props: IUseBundlePackageProps,
  options: UseQueryOptions<IBundlePackage[], Error> = {},
): UseQueryResult<IBundlePackage[], Error> => {
  const { storyId } = props;

  return useQuery<IBundlePackage[], Error>(
    [QueryKey.BundlePackagesByStoryId, storyId],
    async () => {
      if (!storyId) {
        return [];
      }

      try {
        const result = await fetchBundlePackages(storyId);

        if (result.status !== 200) {
          throw new Error('API Error');
        }

        return result.data;
      } catch (e) {
        throw e;
      }
    },
    options,
  );
};

export const useBundlePackageMutation = (): IUseBundlePackageMutation => {
  const snackbar = useSnackbar();
  const backdrop = useBackdrop();
  const queryClient = useQueryClient();

  const createOrUpdate = useMutation<IBundlePackage[], Error, { storyId: number; bundles: IBundlePackage[] }>(
    async ({ storyId, bundles }) => {
      try {
        backdrop.show();
        const result = await createOrUpdateBundlePackage(storyId, bundles);
        const { data } = result.data as ISuccessResult<IBundlePackage[]>;
        return data;
      } catch (e) {
        snackbar.error(e);
        throw e;
      } finally {
        backdrop.hide();
      }
    },
    {
      onSuccess: () => {
        snackbar.success('Updated successfully.');
        invalidateQueriesWithDelay(queryClient, QueryKey.AllBundlePackages);
      },
      onError: (e: Error) => {
        snackbar.error(e.message);
      },
    },
  );

  const remove = useMutation<IBundlePackage, Error, IBundlePackage>(
    async (bundle: IBundlePackage) => {
      try {
        backdrop.show();
        const { id, storyId } = bundle;

        if (!id) {
          throw new Error('Cannot delete the bundle package without id');
        }

        const result = await deleteBundlePackage(storyId, id);
        const { data } = result.data as ISuccessResult<IBundlePackage>;
        return data;
      } catch (e) {
        snackbar.error(e);
        throw e;
      } finally {
        backdrop.hide();
      }
    },
    {
      onSuccess: () => {
        snackbar.success('Delete successfully.');
        invalidateQueriesWithDelay(queryClient, QueryKey.AllBundlePackages);
      },
      onError: (e: Error) => snackbar.error(e.message),
    },
  );

  return {
    createOrUpdate,
    remove,
  };
};
